import { groupBy as _groupBy, sortBy as _sortBy, map as _map } from 'lodash-es';
import { Fragment, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import MailIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import MLink from '@mui/material/Link';
import { makeStyles } from 'tss-react/mui';
import ApproveMemberButton from './approve-member-button';
import DenyMemberButton from './deny-member-button';
import Blockquote from '../blockquote';
import HostUser from '../host-user';
import type { EventMember } from '../../types/EventMember';
import type { Event } from '../../types/Event';

const useStyles = makeStyles()((theme) => ({
  list: {
    listStyleType: 'none',
    margin: theme.spacing(2, 0),
    padding: 0
  },
  listItem: {
    margin: theme.spacing(0, 0, 1.5, 0)
  },
  memberName: {
    display: 'flex',
    alignItems: 'center'
  },
  memberEmail: {
    margin: theme.spacing(1, 0),
    display: 'flex'
  },
  memberPhone: {
    margin: theme.spacing(1, 0),
    display: 'flex'
  },
  emailLink: {
    display: 'inline-flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: '15px'
  },
  emailIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 15
  },
  hostChip: {
    marginLeft: theme.spacing(1)
  },
  requestButtons: {
    marginTop: theme.spacing(1),
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1)
    }
  },
  requestMessage: {
    margin: theme.spacing(1, 0)
  },
}));

type Props = {
  members: EventMember[];
  event: Event;
};

const AttendeesList = ({ members, event }: Props) => {
  const { classes } = useStyles();

  const sortedGroupedMembers = useMemo(() => {
    if (!members || !members.length) {
      return [];
    }

    const sortedMembers = _sortBy(members, (member) => member.isHost ? 0 : 1);
    const membersByStatus = _groupBy(sortedMembers, 'status');
    const sorted = [
      {
        title: 'Going',
        status: 'GOING',
        statusMembers: membersByStatus['GOING'],
      },
      {
        title: 'Interested',
        status: 'INTERESTED',
        statusMembers: membersByStatus['INTERESTED'],
      }
    ];
    if (event.permissions.editable) {
      if (event.registrationType === 'APPROVAL_REQUIRED' && membersByStatus['REQUESTED']) {
        sorted.unshift({
          title: 'Requested',
          status: 'REQUESTED',
          statusMembers: membersByStatus['REQUESTED'],
        });
      }
      sorted.push({
        title: 'Not Going',
        status: 'NOT_GOING',
        statusMembers: membersByStatus['NOT_GOING'],
      });
      if (event.registrationType === 'APPROVAL_REQUIRED' && membersByStatus['DENIED']) {
        sorted.push({
          title: 'Denied',
          status: 'DENIED',
          statusMembers: membersByStatus['DENIED'],
        });
      }
    }
    return sorted;
  }, [event, members]);

  if (!sortedGroupedMembers.length) {
    return <p>No attendees.</p>;
  }

  return (
    <Fragment>
      {_map(sortedGroupedMembers, ({ title, status, statusMembers }) => {
        const totalMemberCount = event.memberSummary[status].count;
        const memberCount = statusMembers ? statusMembers.length : 0;
        const hiddenMemberCount = totalMemberCount - memberCount;
        return (
          <Fragment key={status}>
            <Typography variant="h3">{title} ({totalMemberCount})</Typography>
            {totalMemberCount > 0 ? (
              <ul className={classes.list}>
                <Fragment>
                  {_map(statusMembers, (member) => {
                    return (
                      <li key={member.id} className={classes.listItem}>
                        <div className={classes.memberName}>
                          <HostUser user={member.user} />
                          {member.isHost && (
                            <Chip
                              size="small"
                              label="Host"
                              className={classes.hostChip}
                              color="primary"
                            />
                          )}
                        </div>
                        {member.emailVisible && member.user.email && (
                          <div className={classes.memberEmail}>
                            <MLink href={`mailto:${member.user.email}`} className={classes.emailLink}>
                              <MailIcon className={classes.emailIcon} />
                              {member.user.email}
                            </MLink>
                          </div>
                        )}
                        {member.phoneNumberVisible && member.user.phoneNumberDisplay && (
                          <div className={classes.memberPhone}>
                            <MLink href={member.user.phoneNumberUri} className={classes.emailLink}>
                              <PhoneIcon className={classes.emailIcon} />
                              {member.user.phoneNumberDisplay}
                            </MLink>
                          </div>
                        )}
                        {member.status === 'DENIED' && member.deniedAt && (
                          <Typography variant="caption" color="text.secondary">
                            Denied on {new Date(member.deniedAt).toLocaleDateString()}
                          </Typography>
                        )}
                        {event.permissions.editable && member.status === 'REQUESTED' && (
                          <Fragment>
                            {member.requestMessage && (
                              <Blockquote className={classes.requestMessage}>
                                {member.requestMessage}
                              </Blockquote>
                            )}
                            <div className={classes.requestButtons}>
                              <ApproveMemberButton member={member} />
                              <DenyMemberButton member={member} />
                            </div>
                          </Fragment>
                        )}
                      </li>
                    );
                  })}
                </Fragment>
                {hiddenMemberCount > 0 && (
                  <li>{hiddenMemberCount} hidden</li>
                )}
              </ul>
            ) : (
              <p>None</p>
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
};

export default AttendeesList;
